import { QueryKey } from "@/api";
import { Api } from "@/api/Api";
import { normalizeSavedColors } from "@/overlay/components/GenericColorPicker/savedColors";
import type { SlimAccount, SlimAccountV2, Tags, V2UpdateAccountRequest } from "@mm/metx-workbench.meteomatics.com";
import { type UseMutationOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

export const useSlimAccount = (shouldFetch = true) => {
  const { data, isLoading, error } = useQuery([QueryKey.ACCOUNT_SLIM], () => Api.account.v2GetAccountSlim(), {
    staleTime: Number.POSITIVE_INFINITY,
    enabled: shouldFetch,
  });
  return { data: data, isLoading, error };
};

export const useSavedColorPreferences = () => {
  const qc = useQueryClient();
  const { data: account, isLoading, error } = useSlimAccount();

  const savedColors = useMemo(
    () => normalizeSavedColors(account?.preferences?.color_picker?.saved_colors),
    [account?.preferences?.color_picker?.saved_colors],
  );

  const saveMutation = useMutation<SlimAccount, Error, string[], { snapshot?: SlimAccountV2 }>({
    mutationKey: [QueryKey.ACCOUNT_SLIM, "saved-colors"],
    onMutate: async (nextSavedColors) => {
      await qc.cancelQueries({ queryKey: [QueryKey.ACCOUNT_SLIM] });

      const snapshot = qc.getQueryData<SlimAccountV2>([QueryKey.ACCOUNT_SLIM]);

      if (snapshot) {
        qc.setQueryData<SlimAccountV2>([QueryKey.ACCOUNT_SLIM], {
          ...snapshot,
          preferences: {
            ...(snapshot.preferences ?? {}),
            color_picker: {
              ...(snapshot.preferences?.color_picker ?? {}),
              saved_colors: normalizeSavedColors(nextSavedColors),
            },
          },
        });
      }

      return { snapshot };
    },
    mutationFn: async (nextSavedColors) => {
      if (!account) {
        throw new Error("Account is not loaded");
      }

      const updateReq: V2UpdateAccountRequest = {
        accountId: account.id,
        accountUpdate: {
          preferences: {
            ...(account.preferences ?? {}),
            color_picker: {
              saved_colors: normalizeSavedColors(nextSavedColors),
            },
          },
        },
      };

      return Api.account.v2UpdateAccount(updateReq);
    },
    onError: (_error, _vars, ctx) => {
      if (ctx?.snapshot) {
        qc.setQueryData<SlimAccountV2>([QueryKey.ACCOUNT_SLIM], ctx.snapshot);
      }
    },
    onSuccess: (updatedAccount) => {
      const { profiles: _profiles, ...slimAccount } = updatedAccount;
      qc.setQueryData<SlimAccountV2>([QueryKey.ACCOUNT_SLIM], slimAccount);
    },
  });

  return {
    account,
    savedColors,
    isLoading,
    error,
    saveSavedColors: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
  };
};

export const useAccountTags = () => {
  const { data, isLoading, error } = useQuery<Tags, Error>(
    [QueryKey.AVAILABLE_TAGS],
    async () => {
      const resp = await Api.account.getTagsOfCurrentUser();

      return resp;
    },
    { staleTime: Number.POSITIVE_INFINITY },
  );

  return {
    tags: data || { active: [], available: [] },
    isLoading,
    error,
  };
};

async function setActiveTagsForCurrentAccount(tags: string[]): Promise<Tags> {
  return await Api.account.setActiveTagsOfCurrentAccount({ requestBody: tags });
}

async function addTagToCurrentAccount(tag: string): Promise<Tags> {
  const resp = await Api.account.addTagToCurrentAccount({ tag });

  return resp;
}

async function removeTagFromCurrentAccount(tag: string): Promise<Tags> {
  const resp = await Api.account.removeTagFromCurrentAccount({ tag });

  // Handle both old format (array) and new format (object)
  return resp;
}

async function renameTagInCurrentAccount(oldTag: string, newTag: string): Promise<Tags> {
  const resp = await Api.account.renameTagOfCurrentAccount({ oldTag: oldTag, newTag: newTag });

  return resp;
}

const normalizeTag = (t: string) => t?.trim();

export function useSetActiveAccountTags(
  options?: Omit<UseMutationOptions<Tags, Error, string[], { snapshot: Tags }>, "mutationFn" | "mutationKey">,
) {
  const qc = useQueryClient();

  return useMutation<Tags, Error, string[], { snapshot: Tags }>({
    mutationKey: [QueryKey.AVAILABLE_TAGS, "setActive"],
    mutationFn: setActiveTagsForCurrentAccount,

    // optimistic update
    onMutate: async (rawTags) => {
      const tags = rawTags.map(normalizeTag).filter((t) => t);

      await qc.cancelQueries({ queryKey: [QueryKey.AVAILABLE_TAGS] });
      const snapshot = qc.getQueryData<Tags>([QueryKey.AVAILABLE_TAGS]) ?? { active: [], available: [] };

      qc.setQueryData<Tags>([QueryKey.AVAILABLE_TAGS], (prev) => {
        const curr = prev ?? { active: [], available: [] };
        return {
          ...curr,
          active: tags,
        };
      });

      return { snapshot };
    },
    onError: (error, _vars, ctx) => {
      if (ctx?.snapshot) {
        qc.setQueryData<Tags>([QueryKey.AVAILABLE_TAGS], ctx.snapshot);
      }
      options?.onError?.(error, _vars, ctx);
    },

    onSuccess: (serverResponse) => {
      // Source of truth from server; also guards against accidental nulls/dupes.
      const clean: Tags = {
        active: Array.from(new Set(serverResponse.active)),
        available: Array.from(new Set(serverResponse.available)),
      };
      qc.setQueryData<Tags>([QueryKey.AVAILABLE_TAGS], clean);
    },

    ...options,
  });
}

export function useAddAccountTag(
  options?: Omit<UseMutationOptions<Tags, Error, string, { snapshot: Tags }>, "mutationFn" | "mutationKey">,
) {
  const qc = useQueryClient();

  return useMutation<Tags, Error, string, { snapshot: Tags }>({
    mutationKey: [QueryKey.AVAILABLE_TAGS, "add"],
    mutationFn: addTagToCurrentAccount,

    // optimistic add (with de-dupe)
    onMutate: async (rawTag) => {
      const tag = normalizeTag(rawTag);
      await qc.cancelQueries({ queryKey: [QueryKey.AVAILABLE_TAGS] });

      const snapshot = qc.getQueryData<Tags>([QueryKey.AVAILABLE_TAGS]) ?? { active: [], available: [] };

      if (tag) {
        qc.setQueryData<Tags>([QueryKey.AVAILABLE_TAGS], (prev) => {
          const curr = prev ?? { active: [], available: [] };
          return {
            ...curr,
            available: curr.available?.includes(tag) ? curr.available : [...(curr.available || []), tag],
          };
        });
      }

      return { snapshot };
    },
    onError: (error, _vars, ctx) => {
      if (ctx?.snapshot) {
        qc.setQueryData<Tags>([QueryKey.AVAILABLE_TAGS], ctx.snapshot);
      }
      options?.onError?.(error, _vars, ctx);
    },

    onSuccess: (serverResponse) => {
      // Source of truth from server; also guards against accidental nulls/dupes.
      const clean: Tags = {
        active: Array.from(new Set(serverResponse.active)),
        available: Array.from(new Set(serverResponse.available)),
      };
      qc.setQueryData<Tags>([QueryKey.AVAILABLE_TAGS], clean);
    },

    ...options,
  });
}

export function useRemoveAccountTag() {
  const qc = useQueryClient();

  return useMutation<Tags, Error, string, { previous: Tags }>({
    mutationKey: [QueryKey.AVAILABLE_TAGS, "remove"],
    mutationFn: removeTagFromCurrentAccount,
    onMutate: async (tag) => {
      await qc.cancelQueries({ queryKey: [QueryKey.AVAILABLE_TAGS] });
      const previous = qc.getQueryData<Tags>([QueryKey.AVAILABLE_TAGS]) ?? { active: [], available: [] };

      qc.setQueryData<Tags>([QueryKey.AVAILABLE_TAGS], {
        active: previous.active?.filter((t) => t !== tag),
        available: previous.available?.filter((t) => t !== tag),
      });

      return { previous };
    },
    onError: (_err, _tag, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData<Tags>([QueryKey.AVAILABLE_TAGS], ctx.previous);
      }
    },
    onSuccess: (serverResponse) => {
      const clean: Tags = {
        active: Array.from(new Set(serverResponse.active)),
        available: Array.from(new Set(serverResponse.available)),
      };
      qc.setQueryData<Tags>([QueryKey.AVAILABLE_TAGS], clean);
    },
  });
}

export function useRenameAccountTag(
  options?: Omit<
    UseMutationOptions<Tags, Error, { oldTag: string; newTag: string }, { previous: Tags }>,
    "mutationFn" | "mutationKey"
  >,
) {
  const qc = useQueryClient();

  return useMutation<Tags, Error, { oldTag: string; newTag: string }, { previous: Tags }>({
    mutationKey: [QueryKey.AVAILABLE_TAGS, "rename"],
    mutationFn: ({ oldTag, newTag }) => {
      const oldT = normalizeTag(oldTag);
      const newT = normalizeTag(newTag);
      if (oldT === newT) {
        // No operation if tags are identical
        return Promise.resolve(qc.getQueryData<Tags>([QueryKey.AVAILABLE_TAGS]) ?? { active: [], available: [] });
      }
      return renameTagInCurrentAccount(oldTag, newTag);
    },
    onMutate: async ({ oldTag, newTag }) => {
      const oldT = normalizeTag(oldTag);
      const newT = normalizeTag(newTag);

      await qc.cancelQueries({ queryKey: [QueryKey.AVAILABLE_TAGS] });
      const previous = qc.getQueryData<Tags>([QueryKey.AVAILABLE_TAGS]) ?? { active: [], available: [] };

      // optimistic rename: remove old, add new (de-duped)
      qc.setQueryData<Tags>([QueryKey.AVAILABLE_TAGS], (prev) => {
        const curr = prev ?? { active: [], available: [] };

        const currActive = curr.active ?? [];
        const currAvailable = curr.available ?? [];

        const oldIdxActive = currActive.indexOf(oldT);
        const oldIdxAvailable = currAvailable.indexOf(oldT);

        let newActive = currActive.filter((t) => t !== oldT);
        let newAvailable = currAvailable.filter((t) => t !== oldT);

        if (newT) {
          // Keep order for active: insert at the same index only if old existed in active
          if (oldIdxActive !== -1 && !newActive.includes(newT)) {
            newActive = [...newActive.slice(0, oldIdxActive), newT, ...newActive.slice(oldIdxActive)];
          }

          // Keep order for available: if old existed, insert at the same index; otherwise append (previous behavior)
          if (!newAvailable.includes(newT)) {
            if (oldIdxAvailable !== -1) {
              newAvailable = [...newAvailable.slice(0, oldIdxAvailable), newT, ...newAvailable.slice(oldIdxAvailable)];
            } else {
              newAvailable = [...newAvailable, newT];
            }
          }
        }

        return {
          active: newActive,
          available: newAvailable,
        };
      });

      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData<Tags>([QueryKey.AVAILABLE_TAGS], ctx.previous);
      }
      options?.onError?.(_err, _vars, ctx);
    },
    ...options,
  });
}
